import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { catchError, Observable, tap } from 'rxjs';
// Models
import { User, Organization, Pilot, GenericFileTypeEnum, Metadata, MultipleApiResponse } from '@mocca-technologies/model';


import { LOGO_ENROLMENT, ORGANIZATION_LOGO, PILOT_IMAGE, VEHICLE_IMAGE } from '../utils/routing/routes.model';

@Injectable({
  providedIn: 'root'
})
export class FilesService {


  user = JSON.parse(localStorage.getItem('user')) as User;

  constructor(private http:HttpClient,) { }

  // #region --> Organization

  // #region --> Logo

  /**
   * Uploads a logo image during the enrolment process.
   * Uses the dedicated enrolment logo endpoint.
   * @param file File object to upload
   * @param filename Name of the file
   * @param operator Operator object containing operator information
   * @returns Observable of the HTTP response
   */
  uploadEnrolmentLogo(
    file: File,
    filename: string,
    operator: Organization,
  ): Observable<any> {
    let metadata: Metadata = {
      filename: filename,
      referenceId: operator?.uniqueId,
      secondaryReferenceId: null,
      filetype: GenericFileTypeEnum[GenericFileTypeEnum.FILE_LOGO],
      organizationId: operator?.uniqueId,
      mainOrganizationId: operator?.mainOrganizationUuid,
      signed: false,
      typeKey: GenericFileTypeEnum.FILE_LOGO,
      date: null,
      uniqueId: '',
      droneOrganizationId: operator?.uniqueId,
      infrastructureOrganizationId: null,
      contentType: '',
    };
    const formData: FormData = new FormData();
    const httpParams = new HttpParams()
      .set('metadata', JSON.stringify(metadata))
      .set('organizationId', operator?.uniqueId);
    formData.append('uploaded_file', file);

    const options: Object = {
      withCredentials: true,
      params: httpParams,
      observe: 'response',
    };

    return this.http.post<any>(LOGO_ENROLMENT, formData, options).pipe(
      tap((res) => {
        return res;
      }),
      catchError((error) => {
        throw error;
      }),
    );
  }

  /**
   * This method retrieves the logo of an organization based on the provided organization ID.
   * @returns the logo of the organization in base64
   */
  getDOLogo(organizationId: string) {
    const httpParams = new HttpParams().set('organizationId', organizationId);
    return this.http.get(ORGANIZATION_LOGO, {
      params: httpParams,
      withCredentials: true,
      responseType: 'blob',
    });
  }

  /**
   * Uploads an organization avatar image.
   * @param file File object to upload
   * @param filename Name of the file
   * @param organization Organization object containing organization information
   * @returns Observable of the HTTP response
   */
  uploadOperatorAvatar(file: File, filename: string, organization: Organization ): Observable<any> {
    let metadata: Metadata = {
      filename: filename,
      referenceId: organization?.uniqueId,
      secondaryReferenceId: null,
      filetype: GenericFileTypeEnum[GenericFileTypeEnum.FILE_LOGO],
      organizationId: organization?.uniqueId,
      signed: false,
      typeKey: GenericFileTypeEnum.FILE_LOGO,
      date: null,
      uniqueId: "",
      droneOrganizationId: organization?.uniqueId,
      infrastructureOrganizationId: null,
      contentType: "",
    };
    const formData: FormData = new FormData();
    const httpParams = new HttpParams().set('metadata', JSON.stringify(metadata)).set('organizationId', organization?.uniqueId);
    formData.append('uploaded_file', file);

    // Make sure to set the content type to 'multipart/form-data'
    const options: Object = {
      withCredentials: true, // Optional, if you need to send credentials like cookies
      params: httpParams,
      observe: 'response'
    };

    // Send the POST request with formData
    return this.http.post<any>(ORGANIZATION_LOGO, formData, options).pipe(
      tap((res) => {
        return res;
      }),
      catchError((error) => {
        throw error;
      }
      )
    );
  }

  /**
   * Deletes the avatar image of an organization.
   * @param organizationId unique identifier of the organization
   * @returns
   */
  deleteOperatorAvatar(organizationId: string) {
    const httpParams = new HttpParams().set('organizationId', organizationId);
    return this.http.delete<MultipleApiResponse>(ORGANIZATION_LOGO, {
      params: httpParams,
      withCredentials: true,
      observe: 'response'
    }).pipe(
      tap((res: HttpResponse<MultipleApiResponse>) => {
        return res;
      }),
      catchError((error: HttpErrorResponse) => {
        throw error;
      }
      )
    );
  }

  // #endregion --> Logo

  // #endregion --> Organization

  // #region --> Vehicle

  // #region --> Vehicle Image

  /**
   * This method retrieves the image of a drone based on the provided vehicle ID.
   * @param vehicleUniqueId unique identifier of the vehicle
   * @returns the image of the drone in base64
   */
  getDroneImage(vehicleUniqueId: string){
    const httpParams = new HttpParams().set('vehicleId', vehicleUniqueId);
    return this.http.get(VEHICLE_IMAGE, {
      params: httpParams,
      withCredentials: true,
      responseType: 'blob',
    });
  }

  // #endregion --> Vehicle Image

  // #endregion --> Vehicle

  // #region --> Pilot

  // #region --> Pilot Image

  /**
   *
   * @param pilotUniqueId unique identifier of the pilot
   * @returns the image of the pilot in base64
   */
  getPilotAvatar(pilotUniqueId: string){
    const httpParams = new HttpParams().set('pilotId', pilotUniqueId);
    return this.http.get(PILOT_IMAGE, {
      params: httpParams,
      withCredentials: true,
      responseType: 'blob',
    });
  }

  /**
   * Uploads a pilot avatar image.
   * @param file File object to upload
   * @param filename Name of the file
   * @param pilot Pilot object containing pilot information
   * @returns Observable of the HTTP response
   */
  uploadPilotAvatar(file: File, filename: string, pilot: Pilot ): Observable<any> {
    let metadata: Metadata = {
      filename: filename,
      referenceId: pilot?.uniqueId,
      secondaryReferenceId: null,
      filetype: GenericFileTypeEnum[GenericFileTypeEnum.FILE_PILOT_PHOTO],
      organizationId: pilot?.organizationUuid,
      signed: false,
      typeKey: GenericFileTypeEnum.FILE_PILOT_PHOTO,
      date: null,
      uniqueId: "",
      droneOrganizationId: pilot?.uniqueId,
      infrastructureOrganizationId: null,
      contentType: "",
    };
    const formData: FormData = new FormData();
    const httpParams = new HttpParams().set('metadata', JSON.stringify(metadata)).set('pilotId', pilot?.uniqueId);
    formData.append('file', file);

    // Make sure to set the content type to 'multipart/form-data'
    const options: Object = {
      withCredentials: true, // Optional, if you need to send credentials like cookies
      params: httpParams,
      observe: 'response'
    };

    // Send the POST request with formData
    return this.http.post<any>(PILOT_IMAGE, formData, options).pipe(
      tap((res) => {
        return res;
      }),
      catchError((error) => {
        throw error;
      }
      )
    );
  }

  /**
   * Deletes the avatar image of a pilot.
   * @param pilotId unique identifier of the pilot
   * @returns
   */
  deletePilotAvatar(pilotId: string) {
    const httpParams = new HttpParams().set('pilotId', pilotId);
    return this.http.delete<any>(PILOT_IMAGE, {
      params: httpParams,
      withCredentials: true,
      observe: 'response'
    }).pipe(
      tap((res) => {
        return res;
      }),
      catchError((error) => {
        throw error;
      }
      )
    );
  }
  //#endregion --> Pilot Image

  // #endregion --> Pilot
}
